import { makeStyles } from '@material-ui/core';
import { neutral } from '../../../shared/styles/colors';

import type { OrderDetailToPrint } from './domain';

export default makeStyles({
  invoice: {
    boxSizing: 'border-box',
    margin: '0mm 6mm !important',
    display: 'none',
    gap: '0.5rem',
    flexDirection: 'column',
    alignItems: 'stretch',
    fontWeight: 400,
    fontSize: '0.75rem',
    lineHeight: '0.875rem',
    color: neutral.black,
    // force to apply background color on the printing.
    printColorAdjust: 'exact',
    '-webkit-print-color-adjust': 'exact !important' /* Chrome, Safari */,
    'color-adjust': 'exact !important' /* Firefox */,
    fontFamily: 'Inconsolata, monospace, sans-serif',
    '& *': {
      boxSizing: 'border-box',
      padding: 0,
      margin: 0,
      fontFamily: 'Inconsolata, monospace, sans-serif',
    },
    '& *[lang]': {
      hyphens: 'auto',
    },
    '@media print': {
      display: 'flex',
      '& [aria-label="order-invoice-printing"]': {
        pageBreakInside: 'avoid',
        pageBreakAfter: 'avoid',
        pageBreakBefore: 'avoid',
        breakBefore: 'avoid',
        breakInside: 'avoid',
        breakAfter: 'avoid',
      },
    },
  },

  //++++ HEADER
  header: {
    display: 'flex',
    gap: '0.5rem',
    flexDirection: 'column',
  },
  iconsContainer: {
    display: 'flex',
    gap: '0.25rem',
    alignItems: 'center',
  },
  rappiLogoContainer: {
    width: 'auto',
    height: 'inherit',
    maxWidth: '74px',
    margin: 0,
    '@media print and (width >= 60mm)': {
      maxWidth: '88px',
    },
  },
  turboIconContainer: {
    width: 'auto',
    height: 'inherit',
    maxWidth: '42px',
    margin: 0,
    '@media print and (width >= 60mm)': {
      maxWidth: '50px',
    },
  },
  containerClient: {
    textAlign: 'center',
  },
  userName: {
    fontWeight: 700,
    fontSize: '1.5rem',
    lineHeight: '1.5rem',
  },
  clientTag: {
    fontWeight: 600,
    fontSize: '0.875rem',
    lineHeight: '1.0rem',
  },
  orderId: {
    padding: '0.5rem 0',
    display: 'flex',
    columnGap: '0.75rem',
    flexWrap: 'wrap',
    justifyContent: 'center',
    fontWeight: 400,
    fontSize: '1.25rem',
    lineHeight: '1.25rem',
    border: '1px solid #000',
  },
  moreInfoOrder: {
    display: 'flex',
    gap: '0.125rem',
    flexDirection: 'column',
    fontSize: '0.75rem',
    lineHeight: '0.875rem',
  },
  twoPartsToFlexWrap: {
    display: 'flex',
    columnGap: '0.25rem',
    flexWrap: 'wrap',
  },
  //---- header

  //++++ MAIN
  main: ({ hideCommand }: Props) => ({
    display: hideCommand ? 'none' : 'flex',
    gap: '0.5rem',
    flexDirection: 'column',
  }),
  //++++ ORDER DETAILS
  titleOfDetails: {
    padding: '0.25rem 0.5rem',
    display: 'flex',
    columnGap: '0.25rem',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    fontWeight: 900,
    fontSize: '0.875rem',
    lineHeight: '1rem',
    color: '#FFF',
    backgroundColor: '#000',
  },
  productList: {
    marginTop: '0.5rem',
    listStyleType: 'none',
  },
  product: {
    marginBottom: '0.125rem',
  },
  productDescription: {
    marginBottom: '0.125rem',
    display: 'flex',
    columnGap: '0.5rem',
    justifyContent: 'space-between',
    '& > p[lang]': {
      flexGrow: 1,
    },
  },
  toppingList: {
    margin: '0 0 0.25rem 1.5rem',
    listStyleType: 'none',
    listStylePosition: 'inside',
    '@media print and (width >= 60mm)': {
      margin: '0 0 0.25rem 1.875rem',
    },
  },
  topping: {
    display: 'flex',
    columnGap: '0.5rem',
    flexWrap: 'nowrap',
    justifyContent: 'stretch',
    '& > p[lang]': {
      flexGrow: 1,
    },
    '&::before': {
      content: '"\\02022"',
      width: '0.25rem',
      display: 'inline-block',
      fontSize: '0.875rem',
      '@media print and (width >= 60mm)': {
        width: '0.5rem',
        fontSize: '1rem',
      },
    },
  },
  comment: {
    marginBottom: '0.25rem',
    padding: '0.25rem 0.5rem',
    fontSize: '0.75rem',
    border: '1px solid #000',
  },
  globalOffer: {
    marginBottom: '0.375rem',
    padding: '0.25rem',
    fontSize: '0.75rem',
    borderRight: '2px solid #000',
    borderBottom: '2px solid #000',
  },
  //---- order details

  //++++ WHIMS SECTION
  whimsSection: {
    paddingBottom: '0.25rem',
    borderBottom: '1px solid #000',
  },
  whimsTitle: {
    fontWeight: 700,
    fontSize: '0.875rem',
    lineHeight: '1rem',
    borderBottom: '1px solid #000',
  },
  whim: {
    marginTop: '0.25rem',
    display: 'flex',
    flexWrap: 'nowrap',
    columnGap: '0.25rem',
    justifyContent: 'space-between',
  },
  //---- whims section

  //++++ TOTAL SECTION
  totalsSection: {
    padding: '0.25rem 0',
    display: 'flex',
    rowGap: '0.5rem',
    flexDirection: 'column',
    borderTop: '1px solid #000',
    borderBottom: '1px solid #000',
  },
  totalsError: {
    fontWeight: 600,
    fontSize: '0.75rem',
    lineHeight: '1rem',
  },
  totalsDetails: {
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'space-between',
    fontWeight: 600,
    fontSize: '0.875rem',
    lineHeight: '1rem',
  },
  totalValue: {
    marginTop: '0.125rem',
    padding: '0.25rem',
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'space-between',
    fontWeight: 700,
    fontSize: '1rem',
    color: '#FFF',
    backgroundColor: '#000',
  },
  totalsNotes: {
    marginTop: '0.25rem',
    '& > p': {
      fontSize: '0.75rem',
    },
  },
  //---- total section
  //---- main

  //++++ FOOTER
  //++++ HANDSHAKE CODE SECTION
  handshakeSection: {
    padding: '0.5rem 0 1rem',
  },
  handshakeTitle: {
    fontWeight: 700,
    fontSize: '1.25rem',
    lineHeight: '1.5rem',
    textAlign: 'center',
  },
  handshakeDescription: {
    fontWeight: 600,
    fontSize: '0.75rem',
    lineHeight: '0.875rem',
    textAlign: 'center',
  },
  handshakeCodeContainerFix: ({ hideQRCode }: Props) => ({
    width: '100%',
    height: '140px',
    overflow: 'hidden',
    display: hideQRCode ? 'none' : 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  }),
  qrCode: ({ qrCode, hideQRCode }: Props) => ({
    display: hideQRCode ? 'none' : 'block',
    ...(!hideQRCode && {
      width: '188px',
      height: '188px',
      backgroundSize: 'cover',
      backgroundImage: `url(data:image/jpeg;base64,${qrCode})`,
    }),
  }),
  manualCode: {
    fontWeight: 600,
    fontSize: '0.75rem',
    lineHeight: '0.875rem',
    textAlign: 'center',
  },
  //---- qr code section

  //++++ CHECKLIST
  checklistSection: ({ hideChecklist, hasHandshake }: Props) => ({
    breakBefore: 'page',
    marginTop: '0.25rem',
    paddingTop: hasHandshake ? '0.5rem' : '0.25rem',
    paddingBottom: '0.25rem',
    display: hideChecklist ? 'none' : 'block',
    borderTop: hasHandshake ? '1px solid #000' : 'none',
    borderBottom: '1px solid #000',
  }),
  checklistHeader: {
    marginBottom: '0.25rem',
    textAlign: 'center',
  },
  checklistTitle: {
    padding: '0.25rem 0.5rem',
    display: 'flex',
    columnGap: '0.25rem',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    fontWeight: 700,
    fontSize: '0.75rem',
    lineHeight: '0.875rem',
    border: '1px solid #000',
  },
  checklist: {
    padding: '0.25rem 0',
    listStylePosition: 'inside',
    listStyleType: 'none',
    // listStyleType: '"\\025A1\\000A0"' /* square and space */,
    // options to use as space are \0205F, \000A0, \00020
  },
  checklistItemDescription: {
    display: 'flex',
    columnGap: '0.5rem',
    flexWrap: 'nowrap',
    justifyContent: 'stretch',
    '& > p[lang]': {
      flexGrow: 1,
    },
  },
  checklistDefectRate: {
    marginLeft: '1.25rem',
    display: 'flex',
    columnGap: '0.25rem',
    alignItems: 'stretch',
    fontSize: '0.625rem',
    lineHeight: '0.75rem',
    textTransform: 'uppercase',
  },
  checklistToppings: {
    marginLeft: '1.25rem',
    listStylePosition: 'inside',
    listStyleType: '"\\025A1\\000A0"' /* square and space */,
    '& > li[data-has-price=true]': {
      marginLeft: '-0.675rem',
      /* $ + mini-space + square + space */
      listStyleType: '"$\\0205F\\025A1\\000A0"',
    },
    '@media print and (width >= 60mm)': {
      marginLeft: '1.5rem',
    },
  },
  checklistComment: {
    margin: '0.125rem 0 0.25rem 0.5rem',
    padding: '0.125rem 0.25rem',
    fontSize: '0.625rem',
    lineHeight: '0.75rem',
    borderBottom: '1px solid #000',
    borderRight: '1px solid #000',
  },
  //---- checklist
  //---- footer
});

interface Props {
  hideCommand: boolean;
  hideQRCode: boolean;
  hideChecklist: boolean;
  hasHandshake: boolean;
  qrCode: OrderDetailToPrint['handshake']['qrCode'];
}
